/**
 * Utilidades de formateo para Habitia — Uruguay
 * Centraliza formato de moneda, fechas y numeros para consistencia.
 */

import { getLocaleTag } from "../i18n";

// --- REDONDEO MONETARIO (FIN-001) ---

/**
 * Redondea un valor monetario a 2 decimales de forma segura.
 * Usa Number.EPSILON para mitigar errores de IEEE 754.
 * Usar en TODOS los calculos financieros intermedios y finales.
 */
export const roundMoney = (v) => {
  if (v == null || isNaN(v)) return 0;
  return Math.round((Number(v) + Number.EPSILON) * 100) / 100;
};

// --- MONEDA (UYU) ---

const currencyFormatter = new Intl.NumberFormat("es-UY", {
  style: "currency",
  currency: "UYU",
  maximumFractionDigits: 0,
});

const currencyFormatterDecimals = new Intl.NumberFormat("es-UY", {
  style: "currency",
  currency: "UYU",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Formatea un monto en pesos uruguayos (sin decimales).
 * Ej: 1700 -> "$ 1.700"
 */
export const formatCurrencyUY = (amount) => {
  return currencyFormatter.format(Math.round(amount || 0));
};

/**
 * Formatea un monto en pesos uruguayos (con decimales).
 * Ej: 1700.5 -> "$ 1.700,50"
 */
export const formatCurrencyUYDecimals = (amount) => {
  return currencyFormatterDecimals.format(amount || 0);
};

const usdFormatter = new Intl.NumberFormat("es-UY", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/** Monto en dólares (USD) con formato es-UY. */
export const formatCurrencyUSD = (amount) => {
  return usdFormatter.format(Number(amount || 0));
};

// --- FECHAS ---

/**
 * Formatea una fecha en formato uruguayo dd/MM/yyyy.
 * Acepta Date, Timestamp de Firestore, o string.
 * @param {Date|Object|string} date
 * @param {boolean} includeTime - si true, agrega HH:mm
 */
export const formatDateUY = (date, includeTime = false) => {
  if (!date) return "—";
  let d = date;
  if (date.toDate) d = date.toDate(); // Firestore Timestamp
  if (typeof date === "string") {
    // Strings YYYY-MM-DD se interpretan como UTC; forzar timezone local
    d = /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(date + "T00:00:00") : new Date(date);
  }
  if (!(d instanceof Date) || isNaN(d)) return "—";

  const options = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  };
  if (includeTime) {
    options.hour = "2-digit";
    options.minute = "2-digit";
  }
  return d.toLocaleDateString("es-UY", options);
};

/**
 * Igual que formatDateUY pero en el LOCALE del idioma de UI activo
 * (es-UY / en-US / pt-BR). Usar en pantallas de residente para que las fechas
 * (orden y nombres de mes) sigan el idioma elegido. La MONEDA no cambia (queda
 * UYU/USD con formato es-UY vía formatCurrencyUY).
 * @param {Date|Object|string} date
 * @param {Object} [opts]
 * @param {boolean} [opts.includeTime=false]
 * @param {"numeric"|"long"|"short"} [opts.month] - override del formato de mes
 */
export const formatDateLocalized = (date, opts = {}) => {
  if (!date) return "—";
  let d = date;
  if (date.toDate) d = date.toDate();
  if (typeof date === "string") {
    d = /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(date + "T00:00:00") : new Date(date);
  }
  if (!(d instanceof Date) || isNaN(d)) return "—";

  const options = { day: "2-digit", month: opts.month || "2-digit", year: "numeric" };
  if (opts.includeTime) {
    options.hour = "2-digit";
    options.minute = "2-digit";
  }
  return d.toLocaleDateString(getLocaleTag(), options);
};
